/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/css";
import { Callout, CopyToClipboardButton, MultiSelect, NumberField } from "@octopusdeploy/design-system-components";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { InvitationResource, GetTeamsLookupBffResponseTeam, LicenseUsageResource, LicenseStatusResource, Repository } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { range } from "lodash";
import { useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import { client, repository } from "~/clientInstance";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { PageContent } from "~/components/PageContent/PageContent";
import { SimplePagingList } from "~/components/PagingList";
import PermissionCheck, { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { Section } from "~/components/Section/Section";
import SectionNote from "~/components/SectionNote/SectionNote";
import { UpsellDialogWithLicenseUsage } from "~/components/UpsellDialog/UpsellDialogWithLicenseUsage";
import { EntitlementLimitPopUpEvent } from "~/components/UpsellDialog/upsellDialogAnalytics";
import { getLicenseLimit } from "~/hooks/useLicenseLimit";
interface InviteUserState extends DataBaseComponentState {
    availableTeams: GetTeamsLookupBffResponseTeam[];
    teams: string[];
    quantity: number;
    invites?: InvitationResource[];
    anyAuthenticationProvidersSupportPasswordManagement?: boolean;
}
interface InviteUserPageProps {
    isUserLimitReached: boolean | null;
    openUserLimitDialog: () => void;
}
export interface InviteUsersPageLoaderData {
    licenseUsage: LicenseUsageResource | null;
    licenseStatus: LicenseStatusResource | null;
}
export const inviteUsersPageLoader = async (repository: Repository): Promise<InviteUsersPageLoaderData> => {
    const licenseUsageAsync = repository.Licenses.getCurrentUsage();
    const licenseStatusAsync = repository.Licenses.getCurrentStatus();
    return {
        licenseUsage: await licenseUsageAsync,
        licenseStatus: await licenseStatusAsync,
    };
};
class InviteList extends SimplePagingList<InvitationResource> {
}
class InviteUserPageClass extends DataBaseComponent<InviteUserPageProps, InviteUserState> {
    tenantId: string = null!;
    constructor(props: InviteUserPageProps) {
        super(props);
        this.state = {
            availableTeams: [],
            teams: [],
            quantity: 1,
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            const [availableTeams, document] = await Promise.all([repository.Teams.lookupBff(), repository.Authentication.get()]);
            this.setState({
                availableTeams,
                anyAuthenticationProvidersSupportPasswordManagement: document.AnyAuthenticationProvidersSupportPasswordManagement,
            });
        });
    }
    createInvites = async () => {
        if (this.props.isUserLimitReached) {
            this.props.openUserLimitDialog?.();
            return;
        }
        await this.doBusyTask(async () => {
            // TODO: Need to add a bulk API (comment from old portal, yes we do, but not now)
            const hasSpaceTeam = this.state.teams.map((t) => this.state.availableTeams.find((x) => x.Id === t)).some((t) => t!.SpaceId !== null);
            const invites = await Promise.all(range(0, this.state.quantity)
                //implied rule: any selected teams must all be in the same space or system
                .map((_) => repository.Invitations.invite(this.state.teams, hasSpaceTeam ? repository.spaceId : null)));
            this.setState({
                invites,
            });
        });
    };
    inviteMore = async () => {
        this.setState({
            invites: null!,
            teams: [],
            quantity: 1,
        });
    };
    render() {
        const createInvitesPageAction: PrimaryPageAction | undefined = this.state.anyAuthenticationProvidersSupportPasswordManagement
            ? {
                type: "button",
                label: "Create",
                busyLabel: "Creating...",
                disabled: this.state.busy,
                onClick: () => this.createInvites(),
            }
            : undefined;
        const inviteMorePageAction: PrimaryPageAction = {
            type: "button",
            label: "Invite more users",
            busyLabel: "Setting up...",
            disabled: this.state.busy,
            onClick: () => this.inviteMore(),
        };
        const invitePageAction: PrimaryPageAction | undefined = this.state.invites && this.state.invites.length > 0 ? inviteMorePageAction : createInvitesPageAction;
        const createInvites = (<div>
                <SectionNote>Create invitation codes to allow users to register their own accounts on the Octopus Deploy server. Each code can be used at most once, and all codes will expire 48 hours after creation.</SectionNote>
                <Section>
                    <div className={styles.filters}>
                        <NumberField label="Quantity" value={this.state.quantity} onChange={(quantity) => this.setState({ quantity: quantity ?? 1 })} min={1} max={1000}/>
                        <MultiSelect label="Select teams" items={this.state.availableTeams} getOption={(team) => ({ value: team.Id, label: team.Name })} onChange={(teams) => this.setState({ teams })} value={this.state.teams}/>
                    </div>
                </Section>
            </div>);
        const inviteRequestWithPermissionChecks = !this.state.invites && (<div>
                {/* Yes you need a lot of permissions */}
                <PermissionCheck key="mainTeamCreate" permission={Permission.TeamCreate} alternate={<Section>
                            <Callout type={"information"} title={"Permission required"}>
                                The {Permission.TeamCreate} permission is required to invite users
                            </Callout>
                        </Section>}>
                    <PermissionCheck key="mainTeamEdit" permission={Permission.TeamEdit} alternate={<Section>
                                <Callout type={"information"} title={"Permission required"}>
                                    The {Permission.TeamEdit} permission is required to invite users
                                </Callout>
                            </Section>}>
                        <PermissionCheck key="mainInviteUser" permission={Permission.UserInvite} alternate={<Section>
                                    <Callout type={"information"} title={"Permission required"}>
                                        The {Permission.UserInvite} permission is required to invite users
                                    </Callout>
                                </Section>}>
                            {createInvites}
                        </PermissionCheck>
                    </PermissionCheck>
                </PermissionCheck>
            </div>);
        const resolveLink = (invite: InvitationResource) => `${client.resolve(invite.Links.Register)}`;
        const anchor = (link: string) => (<a href={link} target="_blank" rel={"noopener noreferrer"} onClick={(e) => e.stopPropagation()}>
                {link}
            </a>);
        const createLink = (invite: InvitationResource) => (<div key={invite.InvitationCode} className={styles.linkWithCopy}>
                <div className={styles.copyAction}>
                    <CopyToClipboardButton value={resolveLink(invite)}/>
                </div>
                {anchor(resolveLink(invite))}
            </div>);
        const inviteListing = this.state.invites && this.state.invites.length > 0 && (<div>
                <Section> {this.state.invites.length > 1 ? "Please provide the links below to the invited users." : "Please provide the link below to the invited user."}</Section>
                <InviteList items={this.state.invites} onRow={createLink}/>
            </div>);
        const body = this.state.availableTeams.length > 0 &&
            (this.state.anyAuthenticationProvidersSupportPasswordManagement ? (<div>
                    {inviteRequestWithPermissionChecks}
                    {inviteListing}
                </div>) : (<Section>
                    <Callout title="No Suitable Authentication Providers" type={"warning"}>
                        There are no Authentication Providers that support password management.
                    </Callout>
                </Section>));
        const hasTeamEditAndInviteUser = isAllowed({ permission: Permission.TeamEdit }) && isAllowed({ permission: Permission.TeamCreate }) && isAllowed({ permission: Permission.UserInvite });
        return (<PageContent header={{ title: "Invite Users", primaryAction: hasTeamEditAndInviteUser ? invitePageAction : undefined }} busy={this.state.busy} errors={this.errors}>
                {body}
            </PageContent>);
    }
    static displayName = "InviteUserPageClass";
}
interface InviteUserPageComponentProps {
    loaderData: InviteUsersPageLoaderData;
}
export function InviteUserPage({ loaderData }: InviteUserPageComponentProps) {
    const { isLimitReached: isUserLimitReached, effectiveLicensedLimit } = getLicenseLimit(loaderData.licenseUsage, "users");
    const { isOpen: isUserLimitDialogOpen, openDialog: openUserLimitDialog, closeAllDialogs: closeUserLimitDialog } = useDialogTrigger();
    const simpleDispatchAction = useAnalyticSimpleActionDispatch();
    function openUserLimitDialogAndSendAnalytics() {
        openUserLimitDialog();
        simpleDispatchAction(EntitlementLimitPopUpEvent);
    }
    return (<>
            <InviteUserPageClass isUserLimitReached={isUserLimitReached} openUserLimitDialog={openUserLimitDialogAndSendAnalytics}/>
            <UpsellDialogWithLicenseUsage title="Ready to grow your team?" content={<>
                        You've reached your <strong>{effectiveLicensedLimit}-user</strong> limit. Upgrade to add unlimited users, or remove users who no longer need access.
                    </>} open={isUserLimitDialogOpen} onClose={closeUserLimitDialog} licenseSerialNumber={loaderData.licenseStatus?.SerialNumber} licenseUsage={loaderData.licenseUsage}/>
        </>);
}
const styles = {
    filters: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    linkWithCopy: css({
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    copyAction: css({
        margin: `-${space[16]} ${space[4]} -${space[16]} 0`,
    }),
};
